import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FaRocket, 
  FaCode, 
  FaMobile, 
  FaGlobe, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaGithub,
  FaLinkedin,
  FaTwitter,
  FaArrowUp,
  FaHeart
} from 'react-icons/fa';
import './Footer.css';

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleFooterLinkClick = (href, e) => {
    e.preventDefault();
    if (href.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const emailInput = e.target.querySelector('input[type="email"]');
    if (emailInput && emailInput.value) {
      // Scroll to contact section for newsletter signup
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const contactSection = document.querySelector('#contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        const contactSection = document.querySelector('#contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      emailInput.value = '';
    }
  };

  const footerLinks = {
    services: [
      { name: "Web Applications", href: "#services", type: "scroll" },
      { name: "Mobile Apps", href: "#services", type: "scroll" },
      { name: "Website Design", href: "#services", type: "scroll" },
      { name: "Tech Platforms", href: "#services", type: "scroll" },
      { name: "Cloud Solutions", href: "#services", type: "scroll" },
      { name: "Consulting", href: "#services", type: "scroll" }
    ],
    company: [
      { name: "About Us", href: "#about", type: "scroll" },
      { name: "Our Team", href: "#about", type: "scroll" },
      { name: "Careers", href: "/careers", type: "route" },
      { name: "Contact", href: "#contact", type: "scroll" }
    ],
    support: [
      { name: "Contact Us", href: "#contact", type: "scroll" },
      { name: "Get Support", href: "#contact", type: "scroll" }
    ],
    legal: [
      { name: "Privacy Policy", href: "#contact", type: "scroll" },
      { name: "Terms of Service", href: "#contact", type: "scroll" }
    ]
  };

  const socialLinks = [
    { icon: <FaGithub />, href: "https://github.com", label: "GitHub", target: "_blank", rel: "noopener noreferrer" },
    { icon: <FaLinkedin />, href: "https://linkedin.com", label: "LinkedIn", target: "_blank", rel: "noopener noreferrer" },
    { icon: <FaTwitter />, href: "https://twitter.com", label: "Twitter", target: "_blank", rel: "noopener noreferrer" }
  ];

  const contactInfo = [
    { icon: <FaEnvelope />, text: "info@infinitylogs.com", href: "mailto:info@infinitylogs.com" },
    { icon: <FaPhone />, text: "+45 71573546", href: "tel:+4571573546" },
    { icon: <FaMapMarkerAlt />, text: "Copenhagen, Denmark", href: "#" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        {/* Main Footer Content */}
        <motion.div
          className="footer-main"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Company Info */}
          <motion.div className="footer-brand" variants={itemVariants}>
            <div className="brand-header">
              <Link to="/" className="logo-link">
                <div className="logo">
                  <div className="logo-icon">
                    <FaRocket />
                  </div>
                  <span className="logo-text">Infinity Logs</span>
                </div>
              </Link>
              <p className="brand-description">
                Transforming ideas into digital reality with cutting-edge technology 
                solutions that drive business growth and innovation.
              </p>
            </div>

            {/* Contact Info */}
            <div className="contact-info">
              {contactInfo.map((contact, index) => (
                <a
                  key={index}
                  href={contact.href}
                  className="contact-item"
                >
                  <span className="contact-icon">{contact.icon}</span>
                  <span>{contact.text}</span>
                </a>
              ))}
            </div>

            {/* Social Links */}
            <div className="social-links">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  className="social-link"
                  target={social.target}
                  rel={social.rel}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={social.label}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Footer Links */}
          <div className="footer-links">
            <motion.div className="link-group" variants={itemVariants}>
              <h3 className="link-title">Services</h3>
              <ul className="link-list">
                {footerLinks.services.map((link, index) => (
                  <li key={index}>
                    <a 
                      href={link.href} 
                      className="link-item"
                      onClick={(e) => handleFooterLinkClick(link.href, e)}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div className="link-group" variants={itemVariants}>
              <h3 className="link-title">Company</h3>
              <ul className="link-list">
                {footerLinks.company.map((link, index) => (
                  <li key={index}>
                    {link.type === 'route' ? (
                      <Link to={link.href} className="link-item">{link.name}</Link>
                    ) : (
                      <a 
                        href={link.href} 
                        className="link-item"
                        onClick={(e) => handleFooterLinkClick(link.href, e)}
                      >
                        {link.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div className="link-group" variants={itemVariants}>
              <h3 className="link-title">Support</h3>
              <ul className="link-list">
                {footerLinks.support.map((link, index) => (
                  <li key={index}>
                    <a 
                      href={link.href} 
                      className="link-item"
                      onClick={(e) => handleFooterLinkClick(link.href, e)}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div className="link-group" variants={itemVariants}>
              <h3 className="link-title">Legal</h3>
              <ul className="link-list">
                {footerLinks.legal.map((link, index) => (
                  <li key={index}>
                    <a 
                      href={link.href} 
                      className="link-item"
                      onClick={(e) => handleFooterLinkClick(link.href, e)}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </motion.div>

        {/* Newsletter Signup */}
        <motion.div
          className="newsletter-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="newsletter-content">
            <div className="newsletter-text">
              <h3>Stay Updated</h3>
              <p>Get the latest news, updates, and tech insights delivered to your inbox.</p>
            </div>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email address"
                className="newsletter-input"
                required
              />
              <motion.button
                type="submit"
                className="newsletter-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Subscribe
              </motion.button>
            </form>
          </div>
        </motion.div>

        {/* Footer Bottom */}
        <motion.div
          className="footer-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="bottom-content">
            <div className="copyright">
              <p>
                © 2024 Infinity Logs. All rights reserved. CVR: 45376680. Made with 
                <FaHeart className="heart-icon" /> by our amazing team.
              </p>
            </div>
            
            <div className="tech-stack">
              <span className="tech-label">Powered by:</span>
              <div className="tech-icons">
                <FaCode title="React" />
                <FaGlobe title="Node.js" />
                <FaMobile title="React Native" />
              </div>
            </div>
          </div>

          {/* Scroll to Top Button */}
          <motion.button
            className="scroll-top"
            onClick={scrollToTop}
            whileHover={{ scale: 1.1, y: -2 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <FaArrowUp />
          </motion.button>
        </motion.div>
      </div>

      {/* Background Elements */}
      <div className="footer-bg">
        <div className="bg-orb orb-1"></div>
        <div className="bg-orb orb-2"></div>
      </div>
    </footer>
  );
};

export default Footer;
